import styles from './InvestigationScaleMarker.module.scss';
import { AssetInvestigationType, RealSizeCompatible } from '@customer/store/apis/investigationsApi';
import { FeatureGroup, SVGOverlay, Tooltip } from 'react-leaflet';
import { latLng, LatLngBounds } from 'leaflet';
import { useTranslation } from 'react-i18next';
import {
  AssetTypeName,
  InvestigationAssetMarkerProps,
} from '../investigation-asset-marker/InvestigationAssetMarker';
import { ObjectTypeScaleMarker, getObjectTypeScaleRadius } from '@dmc/ui';
import { useMemo } from 'react';

export interface InvestigationScaleMarkerProps
  extends InvestigationAssetMarkerProps,
    RealSizeCompatible {}

export const InvestigationScaleMarker = ({
  name,
  position,
  heading,
  type,
  obj,
  yrot,
  wid: bodyWidth,
  len: bodyLength,
  boomlen: boomLength = 0,
  pane,
  investigationType,
}: InvestigationScaleMarkerProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  const assetType = AssetTypeName[type];

  // Calculate the radius of the SVG display area in units of metres.
  const svgRadius = useMemo<number>(() => {
    return getObjectTypeScaleRadius({ bodyWidth, bodyLength, boomLength, yrot });
  }, [bodyWidth, bodyLength, boomLength, yrot]);

  // Generate lat/lng bounds by projecting distance in metres from the center point.
  const svgBounds = useMemo<LatLngBounds>(() => {
    return latLng(position).toBounds(svgRadius * 2);
  }, [position, svgRadius]);

  // Only add defined pane props to Leaflet components to avoid runtime errrors.
  const paneProp = pane ? { pane } : {};
  const isCustom = investigationType === AssetInvestigationType.Custom;

  return (
    // Wrap the SVGOverlay and Tooltip in a FeatureGroup so that the Tooltip can be positioned relative to the SVG ViewBox center.
    <FeatureGroup>
      <SVGOverlay
        {...paneProp}
        className={`${styles.svgReset} ${styles.disablePointerEvents}`}
        bounds={svgBounds}
        interactive={true}
      >
        <ObjectTypeScaleMarker
          yrot={yrot}
          heading={heading ?? 0}
          objectType={obj}
          bodyWidth={bodyWidth}
          bodyLength={bodyLength}
          boomLength={boomLength}
          imageClassName={styles.enablePointerEvents}
          showYRot={true}
        />
      </SVGOverlay>
      {/* This tooltip should be automatically centered to the SVGOverlay center. */}
      <Tooltip className="tooltip-override">
        <div className={styles.popup}>
          {!isCustom && <span>{t(assetType)}</span>}
          <span className={`${isCustom ? styles['popup__name--compact'] : styles['popup__name']}`}>
            {name}
          </span>
        </div>
      </Tooltip>
    </FeatureGroup>
  );
};
